import { useTraduccion } from '@tallerya/i18n';
import { AvisoError, Boton, Buscador, Chip, Filtro, IconoMas, Listado, useBusquedaDiferida, type Columna, type ElementoMenu } from '@tallerya/ui';
import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import { useTituloPantalla } from '../../layout/titulo';
import { useAccionDeFila } from '../../componentes/useAccionDeFila';
import { useTipos } from '../catalogos/datos';
import { useActivos, useCambiosActivos, type Activo, type FiltrosActivos } from './datos';
import { EliminarActivo } from './EliminarActivo';

// «Activos» (#119): los vehículos y equipos del taller. No hay frame en el
// Figma —ahí los activos solo aparecen dentro de la ficha del cliente—, y se
// decidió con Mario tener además este listado: recepción busca por la placa sin
// saber de quién es el carro.
//
// La tabla la dibuja `Listado`, y la paginación es por cursor. **Los filtros
// viven en la URL** (`?buscar=&tipo=<id>&estado=activos`), como en Clientes:
// desde que la fila abre la ficha (#187), «Atrás» tiene que traerlos de vuelta, y
// en el estado de la pantalla se perdían al salir de ella (#188). La página no:
// el cursor es opaco, y al volver se empieza por la primera.

/** Los filtros de la URL. Los parámetros se llaman como los campos. */
type FiltrosUrl = Omit<FiltrosActivos, 'cursor'>;

/** El id de un tipo. Lo demás no se le manda a la API. */
const ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Un valor que no se reconoce —un enlace viejo o editado a mano— cuenta como «Todos». */
function leerFiltros(params: URLSearchParams): FiltrosUrl {
  const tipo = params.get('tipo');
  const estado = params.get('estado');
  return {
    buscar: params.get('buscar') ?? '',
    tipo: tipo && ID.test(tipo) ? tipo : 'todos',
    estado: estado === 'activos' || estado === 'inactivos' ? estado : 'todos',
  };
}

export function ListadoActivos() {
  const { t } = useTraduccion();
  const navegar = useNavigate();
  const cambios = useCambiosActivos();
  const tipos = useTipos();
  useTituloPantalla(t('admin.activos.titulo'));

  const [params, setParams] = useSearchParams();
  const url = leerFiltros(params);
  const busqueda = params.toString();

  const [texto, setTexto] = useState(url.buscar);
  const [cursor, setCursor] = useState<string | null>(null);
  // La API solo sabe ir hacia adelante: volver es sacar el último cursor de esta pila.
  const [anteriores, setAnteriores] = useState<(string | null)[]>([]);
  const [eliminando, setEliminando] = useState<Activo | null>(null);
  const { hacer, problema } = useAccionDeFila();

  // Cuando la URL cambia —un filtro, «Atrás», o tocar «Activos» en el menú, que
  // la deja limpia— se vuelve a la primera página, y la caja muestra el texto de
  // la URL. Se ajusta al dibujar, como en `ListadoClientes`, y no en un efecto
  // que dibujaría primero lo viejo.
  const [vista, setVista] = useState({ busqueda, buscar: url.buscar });
  if (busqueda !== vista.busqueda) {
    setVista({ busqueda, buscar: url.buscar });
    setCursor(null);
    setAnteriores([]);
    if (url.buscar !== vista.buscar) setTexto(url.buscar);
  }

  /** Reemplaza la entrada del historial: «Atrás» sale del listado, no deshace un filtro por tecla. */
  function filtrar(cambio: Partial<FiltrosUrl>) {
    setParams(
      (actuales) => {
        const nuevos = new URLSearchParams(actuales);
        for (const [clave, valor] of Object.entries(cambio)) {
          // «Todos» es el valor de los combos; en el buscador es un texto como cualquiera.
          if (valor && (clave === 'buscar' || valor !== 'todos')) nuevos.set(clave, valor);
          else nuevos.delete(clave);
        }
        return nuevos;
      },
      { replace: true },
    );
  }

  useBusquedaDiferida(texto, (buscar) => filtrar({ buscar }), { pendiente: texto !== url.buscar });

  const filtros: FiltrosActivos = { ...url, cursor };
  const consulta = useActivos(filtros);
  const pagina = consulta.data;

  const nombreDe = (a: Activo) => [a.code, a.identifier].filter(Boolean).join(' · ');

  // `desdeListado`: la ficha y el formulario vuelven con «Atrás», y el listado
  // regresa con sus filtros. Si el activo se elimina desde su edición, se salta
  // hasta acá en vez de a una ficha que ya no existe.
  const verFicha = (a: Activo) => void navegar(`/activos/${String(a.id)}`, { state: { desdeListado: true } });
  const editar = (a: Activo) => void navegar(`/activos/${String(a.id)}/editar`, { state: { desdeListado: true } });

  function opciones(a: Activo): ElementoMenu[] {
    const id = String(a.id);
    const nombre = { nombre: nombreDe(a) };
    return [
      { etiqueta: t('admin.activos.menu.verFicha'), onSeleccionar: () => verFicha(a) },
      { etiqueta: t('admin.activos.menu.editar'), onSeleccionar: () => editar(a) },
      a.isActive
        ? {
            etiqueta: t('admin.activos.menu.desactivar'),
            onSeleccionar: () => void hacer(cambios.cambiarEstado(id, false), t('admin.activos.aviso.desactivado', nombre)),
          }
        : {
            etiqueta: t('admin.activos.menu.activar'),
            onSeleccionar: () => void hacer(cambios.cambiarEstado(id, true), t('admin.activos.aviso.activado', nombre)),
          },
      { tipo: 'divisor' as const },
      { etiqueta: t('admin.activos.menu.eliminar'), peligro: true, onSeleccionar: () => setEliminando(a) },
    ];
  }

  const columnas: Columna<Activo>[] = [
    {
      id: 'codigo',
      titulo: t('admin.activos.columna.codigo'),
      ancho: 110,
      // Un botón, para llegar con el teclado: la fila entera solo responde al dedo y al mouse.
      celda: (a) => (
        <button type="button" className="tipos__enlace" onClick={() => verFicha(a)}>
          {a.code ?? '—'}
        </button>
      ),
    },
    {
      id: 'identificador',
      titulo: t('admin.activos.columna.identificador'),
      ancho: 150,
      // La etiqueta del tipo y no «Placa» fija: un equipo se identifica por su serie.
      celda: (a) =>
        a.identifier ? (
          <span className="activos__identificador">
            <small>{a.identifierLabel}</small>
            {a.identifier}
          </span>
        ) : (
          '—'
        ),
      // En la tarjeta, la placa sola: «Placa» encima no cabe en el renglón del título.
      // Sin identificador, el código, para que la tarjeta tenga con qué reconocerse.
      tarjeta: { lugar: 'titulo', celda: (a) => a.identifier || a.code || '—' },
    },
    {
      id: 'tipo',
      titulo: t('admin.activos.columna.tipo'),
      ancho: 120,
      celda: (a) => <Chip>{a.assetTypeName}</Chip>,
      // Texto y no etiqueta: en el pie va en gris junto a los demás datos.
      tarjeta: { lugar: 'pie', celda: (a) => a.assetTypeName },
    },
    {
      id: 'marca',
      titulo: t('admin.activos.columna.marcaModelo'),
      celda: (a) => [a.brandName, a.modelName].filter(Boolean).join(' ') || '—',
      tarjeta: 'renglon',
    },
    { id: 'anio', titulo: t('admin.activos.columna.anio'), ancho: 70, alinear: 'centro', celda: (a) => a.year ?? '—' },
    {
      id: 'cliente',
      titulo: t('admin.activos.columna.cliente'),
      celda: (a) => a.customerName ?? '—',
      // Primero en el pie, antes que el tipo: es por quien se pregunta.
      tarjeta: { lugar: 'pie', orden: 1 },
    },
    {
      id: 'estado',
      titulo: t('admin.activos.columna.estado'),
      ancho: 110,
      celda: (a) =>
        a.isActive ? <Chip tono="exito">{t('admin.activos.activo')}</Chip> : <Chip>{t('admin.activos.inactivo')}</Chip>,
      tarjeta: 'esquina',
    },
  ];

  const botonNuevo = (
    <Boton icono={<IconoMas />} onClick={() => void navegar('/activos/nuevo')}>
      {t('admin.activos.nuevo')}
    </Boton>
  );

  return (
    <>
      {problema && <AvisoError aviso={{ titulo: t('admin.activos.error.titulo'), texto: problema }} />}

      <Listado
        titulo={t('admin.activos.titulo')}
        info={t('admin.activos.info')}
        columnas={columnas}
        filas={pagina?.items}
        claveFila={(a) => String(a.id)}
        estado={consulta.isPending ? 'cargando' : consulta.isError ? 'error' : 'listo'}
        onReintentar={() => void consulta.refetch()}
        filtros={
          <>
            <Buscador valor={texto} onCambiar={setTexto} placeholder={t('admin.activos.buscar')} />
            <Filtro
              etiqueta={t('admin.activos.filtro.tipo')}
              valor={filtros.tipo}
              onCambiar={(tipo) => filtrar({ tipo })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.activos.filtro.todos') },
                ...(tipos.data ?? []).map((tp) => ({ valor: String(tp.id), etiqueta: tp.name })),
              ]}
            />
            <Filtro
              etiqueta={t('admin.activos.filtro.estado')}
              valor={filtros.estado}
              onCambiar={(estado) => filtrar({ estado: estado as FiltrosActivos['estado'] })}
              opciones={[
                { valor: 'todos', etiqueta: t('admin.activos.filtro.todos') },
                { valor: 'activos', etiqueta: t('admin.activos.activo') },
                { valor: 'inactivos', etiqueta: t('admin.activos.inactivo') },
              ]}
            />
          </>
        }
        acciones={botonNuevo}
        // Una página que no es la primera cuenta como filtro, como en Sucursales.
        hayFiltros={filtros.buscar !== '' || filtros.tipo !== 'todos' || filtros.estado !== 'todos' || filtros.cursor !== null}
        onLimpiarFiltros={() => {
          setTexto('');
          // La primera página aunque la URL ya estuviera limpia: limpiar desde
          // una página 2 vacía también tiene que volver al inicio.
          setCursor(null);
          setAnteriores([]);
          setParams({}, { replace: true });
        }}
        vacio={{ titulo: t('admin.activos.vacio.titulo'), texto: t('admin.activos.vacio.texto'), acciones: botonNuevo }}
        {...(filtros.buscar && { detalleSinResultados: t('admin.activos.sinResultados', { texto: filtros.buscar }) })}
        opcionesFila={opciones}
        onFila={verFicha}
        paginacion={{
          hayAnterior: anteriores.length > 0,
          hayMas: pagina?.hasMore ?? false,
          onAnterior: () => {
            setCursor(anteriores.at(-1) ?? null);
            setAnteriores((a) => a.slice(0, -1));
          },
          onSiguiente: () => {
            if (!pagina?.nextCursor) return;
            setAnteriores((a) => [...a, cursor]);
            setCursor(pagina.nextCursor);
          },
        }}
      />

      <EliminarActivo activo={eliminando} onCerrar={() => setEliminando(null)} />
    </>
  );
}
